import {
  memo,
  cloneElement,
  HTMLAttributes,
  RefAttributes,
  ReactElement,
} from "react";
import { createPortal } from "react-dom";
import { TooltipProps } from "./types";
import { useTooltip } from "./useTooltip";
import { TooltipContent } from "./TooltipContent";
import { getTooltipPortalRoot } from "./getTooltipPortalRoot";
import { twMerge } from "tailwind-merge";
import { TOOLTIP_PLACEMENT, TOOLTIP_THEME } from "./constants";

const Tooltip = memo(
  ({
    message,
    children,
    placement = TOOLTIP_PLACEMENT.TOP,
    theme = TOOLTIP_THEME.DARK,
    showArrow = true,
    className,
    disabled = false,
    portaled = false,
    anchor,
    tooltipClassName,
  }: TooltipProps) => {
    const portalTarget =
      anchor ??
      (portaled && typeof document !== "undefined"
        ? getTooltipPortalRoot()
        : undefined);
    const isPortaled = Boolean(portalTarget);

    const {
      tooltipVisible,
      tooltipPosition,
      childrenDisplayStyle,
      tooltipsRef,
      childRef,
      onMouseHover,
      onMouseLeave,
    } = useTooltip({ placement, showArrow, children, isPortaled });

    if (disabled) return children;

    const isInline = ["inline", "inline-block"].includes(childrenDisplayStyle);

    const content = (
      <TooltipContent
        message={message}
        placement={placement!}
        theme={theme!}
        showArrow={showArrow}
        isPortaled={isPortaled}
        tooltipPosition={tooltipPosition}
        tooltipRef={tooltipsRef}
        tooltipClassName={tooltipClassName}
      />
    );

    return (
      <div
        className={twMerge(
          "group relative",
          isInline && "inline-block",
          className
        )}
        onMouseEnter={onMouseHover}
        onMouseLeave={onMouseLeave}
      >
        {cloneElement(
          children as ReactElement<
            HTMLAttributes<HTMLElement> & RefAttributes<HTMLElement>
          >,
          {
            ref: childRef,
          }
        )}
        {tooltipVisible &&
          (portalTarget ? createPortal(content, portalTarget) : content)}
      </div>
    );
  }
);

Tooltip.displayName = "Tooltip";

export default Tooltip;
